import {
  addMinutesToTime,
  addWeeks,
  compareDates,
  crossesMidnight,
  formatLong,
  weekdayOf,
} from '@/domain/dates';
import {
  type Assignment,
  DAY_TYPES,
  DEFAULT_SETTINGS,
  type IsoDate,
  type Rider,
  type Settings,
  type Volunteer,
} from '@/shared/types';

/**
 * Calendar sync planning (spec 7). Pure: it is handed a snapshot of what is
 * currently on the calendars and returns only the differences, so the whole
 * lifecycle is unit-testable and the sync report tells the truth about what
 * actually changed.
 *
 * DESIGN NOTE — deviation from the spec.
 * The spec models the public calendar as one recurring "?" series whose
 * individual occurrences get retitled, and section 10 flags occurrence-ID
 * fragility as the main risk to de-risk before building. That design exists
 * because Apps Script had to piggyback on a series a human created by hand.
 * This app owns the calendar outright, so it writes one standalone event per
 * meeting date instead. Same visible result, but the IDs are stable, nothing
 * can fork a series, and reverting a title is an ordinary patch. The spike's
 * hardest open question is removed rather than answered.
 */

export type SyncOpKind =
  | 'public.create'
  | 'public.retitle'
  | 'public.delete'
  | 'invite.create'
  | 'invite.update'
  | 'invite.replace'
  | 'invite.delete';

/** Somebody who gets their own copy of a private invite. */
export interface InviteGuest {
  email: string;
  name?: string;
  role: 'volunteer' | 'rider';
  /**
   * Their answer on the event as it stands. A patch replaces the guest list
   * outright, so an answer left off here is an answer thrown away: the
   * volunteer's "yes" would revert to an unanswered invitation.
   */
  responseStatus?: string;
}

export interface SyncOp {
  kind: SyncOpKind;
  date: IsoDate;
  riderId: string;
  eventId?: string;
  title?: string;
  description?: string;
  /** Everyone invited to this ride: the driver, and the rider when reachable. */
  attendees?: InviteGuest[];
  /** Guests being removed, on a replace or a delete. */
  previousAttendeeEmails?: string[];
  /** Plain-language reason, shown in the sync report. */
  reason: string;
}

/** What is currently on the public calendar for one assignment. */
export interface RemotePublicEvent {
  summary: string;
}

/** What is currently on the private calendar for one assignment. */
export interface RemoteInviteEvent {
  summary: string;
  attendeeEmails: string[];
  /** Each guest's answer so far, by lowercased address. */
  responses?: Record<string, string>;
}

export interface RemoteState {
  publicEvents: Map<string, RemotePublicEvent>;
  inviteEvents: Map<string, RemoteInviteEvent>;
}

/**
 * Somebody who should have been invited but has no address on file. Surfaced,
 * never swallowed: a volunteer with no email gets no invite at all, and a rider
 * with no email is simply left off the guest list.
 */
export interface MissingEmail {
  date: IsoDate;
  name: string;
  role: 'volunteer' | 'rider';
}

export interface SyncPlan {
  from: IsoDate;
  to: IsoDate;
  ops: SyncOp[];
  /** Assignments in the horizon already in the right state. */
  unchanged: number;
  missingEmails: MissingEmail[];
}

export const UNASSIGNED_TITLE = '?';

export function publicTitleFor(assignment: Assignment, volunteer: Volunteer | undefined): string {
  // Assigned-but-unconfirmed stays "?" publicly, so the shared view never
  // shows a name that has not actually been agreed to yet (spec 7.1).
  if (!assignment.confirmed || !volunteer) return UNASSIGNED_TITLE;
  const suffix = assignment.legs === 'Both' ? '' : ` (${assignment.legs.toLowerCase()})`;
  return `${volunteer.name}${suffix}`;
}

export function inviteTitleFor(assignment: Assignment, rider: Rider | undefined): string {
  const who = rider?.name ?? 'rider';
  if (assignment.legs === 'Pickup only') return `Pick up ${who}`;
  if (assignment.legs === 'Dropoff only') return `Drop off ${who}`;
  return `Ride for ${who}`;
}

export function inviteDescriptionFor(
  assignment: Assignment,
  rider: Rider | undefined,
  volunteer?: Volunteer | undefined,
): string {
  // The rider is on this invite too, so it names both sides rather than
  // assuming the reader already knows who the other one is.
  return [
    `${formatLong(assignment.date)} — ${assignment.legs.toLowerCase()}.`,
    volunteer ? `Driving: ${volunteer.name}${volunteer.phone ? ` (${volunteer.phone})` : ''}` : '',
    rider ? `Riding: ${rider.name}${rider.phone ? ` (${rider.phone})` : ''}` : '',
    rider?.notes ? `Notes: ${rider.notes}` : '',
    assignment.notes ? `This ride: ${assignment.notes}` : '',
    '',
    'If you cannot make it, please let the coordinator know as early as you can.',
  ]
    .filter(Boolean)
    .join('\n');
}

export interface SyncPlanOptions {
  /** Overrides the horizon end; defaults to the configured sync horizon. */
  to?: IsoDate;
  /** Current calendar contents. Empty maps mean "nothing exists yet". */
  remote?: RemoteState;
}

const EMPTY_REMOTE: RemoteState = { publicEvents: new Map(), inviteEvents: new Map() };

function normalizeEmail(value: string | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

function includesEmail(remote: readonly string[], email: string): boolean {
  return remote.some((candidate) => normalizeEmail(candidate) === normalizeEmail(email));
}

/** Carry each guest's existing answer onto the list we are about to write. */
function withExistingAnswers(
  guests: readonly InviteGuest[],
  remote: RemoteInviteEvent,
): InviteGuest[] {
  return guests.map((guest) => {
    const answer = remote.responses?.[normalizeEmail(guest.email)];
    return answer ? { ...guest, responseStatus: answer } : guest;
  });
}

/** Guest lists match when they carry the same addresses, in any order. */
function sameGuests(remote: readonly string[], wanted: readonly InviteGuest[]): boolean {
  const before = [...new Set(remote.map(normalizeEmail).filter(Boolean))].sort();
  const after = [...new Set(wanted.map((guest) => normalizeEmail(guest.email)))].sort();
  return before.length === after.length && before.every((email, i) => email === after[i]);
}

/**
 * Decide the calendar work for the notice window.
 *
 * Only the sync horizon is touched: volunteers see roughly four weeks ahead
 * while the coordinator works three months out, so anything past the horizon
 * is deliberately invisible.
 */
export function planCalendarSync(
  assignments: readonly Assignment[],
  volunteers: readonly Volunteer[],
  riders: readonly Rider[],
  settings: Settings,
  today: IsoDate,
  options: SyncPlanOptions = {},
): SyncPlan {
  const to = options.to ?? addWeeks(today, settings.calendarSyncHorizonWeeks);
  const remote = options.remote ?? EMPTY_REMOTE;
  const volunteerById = new Map(volunteers.map((v) => [v.id, v]));
  const riderById = new Map(riders.map((r) => [r.id, r]));

  const plan: SyncPlan = { from: today, to, ops: [], unchanged: 0, missingEmails: [] };
  // A rider with no address would otherwise be reported on every single date.
  const ridersReported = new Set<string>();

  for (const assignment of assignments) {
    if (compareDates(assignment.date, today) < 0) continue;
    if (compareDates(assignment.date, to) > 0) continue;

    const volunteer = assignment.volunteerId
      ? volunteerById.get(assignment.volunteerId)
      : undefined;
    const rider = riderById.get(assignment.riderId);
    const before = plan.ops.length;

    // --- public calendar: title only, never any guests ---------------------
    const publicEvent = assignment.publicEventId
      ? remote.publicEvents.get(assignment.publicEventId)
      : undefined;

    if (assignment.outcome === 'No Meeting') {
      if (assignment.publicEventId && publicEvent) {
        plan.ops.push({
          kind: 'public.delete',
          date: assignment.date,
          riderId: assignment.riderId,
          eventId: assignment.publicEventId,
          reason: 'no meeting on this date',
        });
      }
    } else {
      const title = publicTitleFor(assignment, volunteer);

      if (!assignment.publicEventId || !publicEvent) {
        plan.ops.push({
          kind: 'public.create',
          date: assignment.date,
          riderId: assignment.riderId,
          title,
          reason: assignment.publicEventId
            ? 'public event was deleted; recreating'
            : 'adding the placeholder to the public calendar',
        });
      } else if (publicEvent.summary !== title) {
        plan.ops.push({
          kind: 'public.retitle',
          date: assignment.date,
          riderId: assignment.riderId,
          eventId: assignment.publicEventId,
          title,
          reason:
            title === UNASSIGNED_TITLE
              ? 'no longer confirmed: back to "?"'
              : 'confirmed: showing the name publicly',
        });
      }
    }

    // --- private invite: the volunteer's own copy --------------------------
    const inviteEvent = assignment.inviteEventId
      ? remote.inviteEvents.get(assignment.inviteEventId)
      : undefined;
    const wantsInvite = Boolean(volunteer) && assignment.outcome !== 'No Meeting';

    if (!wantsInvite) {
      if (assignment.inviteEventId && inviteEvent) {
        plan.ops.push({
          kind: 'invite.delete',
          date: assignment.date,
          riderId: assignment.riderId,
          eventId: assignment.inviteEventId,
          previousAttendeeEmails: inviteEvent.attendeeEmails,
          reason: volunteer ? 'meeting canceled' : 'nobody assigned',
        });
      }
    } else if (!volunteer!.email) {
      plan.missingEmails.push({
        date: assignment.date,
        name: volunteer!.name,
        role: 'volunteer',
      });
    } else {
      // The rider gets their own copy of the invite whenever we have an address
      // for them, so both sides of the ride see the same event.
      const guests: InviteGuest[] = [
        { email: volunteer!.email, name: volunteer!.name, role: 'volunteer' },
      ];

      if (rider?.email) {
        guests.push({ email: rider.email, name: rider.name, role: 'rider' });
      } else if (rider && !ridersReported.has(rider.id)) {
        ridersReported.add(rider.id);
        plan.missingEmails.push({ date: assignment.date, name: rider.name, role: 'rider' });
      }

      const common = {
        date: assignment.date,
        riderId: assignment.riderId,
        title: inviteTitleFor(assignment, rider),
        description: inviteDescriptionFor(assignment, rider, volunteer),
        attendees: guests,
      };

      if (!assignment.inviteEventId || !inviteEvent) {
        plan.ops.push({
          ...common,
          kind: 'invite.create',
          reason: 'assignment made: sending the heads-up',
        });
      } else if (!includesEmail(inviteEvent.attendeeEmails, volunteer!.email)) {
        // Cancel and recreate rather than swapping the guest list in place.
        // A cancellation is unambiguous: the previous volunteer's copy leaves
        // their calendar instead of lingering as declined clutter (spec 7.2).
        plan.ops.push({
          ...common,
          kind: 'invite.replace',
          eventId: assignment.inviteEventId,
          previousAttendeeEmails: inviteEvent.attendeeEmails,
          reason: 'volunteer changed: cancelling the old invite and sending a new one',
        });
      } else if (
        inviteEvent.summary !== common.title ||
        !sameGuests(inviteEvent.attendeeEmails, guests)
      ) {
        // The driver is unchanged, so patching in place is enough: nobody's
        // copy needs to be withdrawn, and a guest added or removed here is
        // told by Google on the same update. Answers already given are carried
        // back over, because the patch replaces the whole guest list.
        plan.ops.push({
          ...common,
          kind: 'invite.update',
          eventId: assignment.inviteEventId,
          attendees: withExistingAnswers(guests, inviteEvent),
          reason: sameGuests(inviteEvent.attendeeEmails, guests)
            ? 'ride details changed'
            : 'guest list changed',
        });
      }
    }

    if (plan.ops.length === before) plan.unchanged++;
  }

  return plan;
}

/**
 * The start time that applies to one date.
 *
 * Thursday and Sunday meetings run at different hours, so the time is looked up
 * by weekday rather than shared. A date that is not a meeting day at all (a row
 * added by hand) borrows the first configured day's time instead of inventing
 * one.
 */
export function meetingTimeFor(date: IsoDate, settings: Settings): string {
  const weekday = weekdayOf(date);
  const day = DAY_TYPES.find((candidate) => candidate === weekday) ?? settings.meetingDays[0];
  return (day ? settings.meetingTimes[day] : undefined) ?? DEFAULT_SETTINGS.meetingTimes.Thursday;
}

/** Start and end timestamps for a meeting, as naive local wall-clock times. */
export function eventWindow(
  date: IsoDate,
  settings: Settings,
): { start: string; end: string; endDate: IsoDate } {
  const startTime = meetingTimeFor(date, settings);
  const endTime = addMinutesToTime(startTime, settings.meetingDurationMinutes);
  // A meeting that runs past midnight has to land its end on the next day, or
  // Google rejects the event outright.
  const endDate = crossesMidnight(startTime, settings.meetingDurationMinutes)
    ? addDaysIso(date, 1)
    : date;

  return {
    start: `${date}T${startTime}:00`,
    end: `${endDate}T${endTime}:00`,
    endDate,
  };
}

function addDaysIso(date: IsoDate, days: number): IsoDate {
  const ms = Date.UTC(
    Number(date.slice(0, 4)),
    Number(date.slice(5, 7)) - 1,
    Number(date.slice(8, 10)) + days,
  );
  return new Date(ms).toISOString().slice(0, 10);
}
